import React, { useEffect, useState } from 'react'
import FormSection from '../components/FormSection'
import {
  fetchScopeAliasReview,
  submitScopeAliasReviewActions
} from '../services/procurementBidsClient'
import '../styles/utilities.css'

const FILTER_OPTIONS = [
  { key: 'all', label: 'All Lines' },
  { key: 'gaps', label: 'Gaps' },
  { key: 'review', label: 'Needs Review' },
  { key: 'ready', label: 'Clear Matches' }
]
const DEFAULT_PERSISTENCE_MODE = 'response_only'

function formatDecisionLabel(value) {
  return String(value || '')
    .replace(/_/g, ' ')
    .trim() || '--'
}

function normalizeText(value) {
  return String(value || '').trim().toLowerCase()
}

function getProposalState(item) {
  const proposal = item?.proposal || {}
  const confidence = Number(proposal.mapping_confidence || 0)

  if (proposal.is_gap_flag) {
    return 'gaps'
  }

  if (confidence < 0.85) {
    return 'review'
  }

  return 'ready'
}

function formatConfidence(value) {
  const number = Number(value)
  if (!Number.isFinite(number)) {
    return '--'
  }

  return `${Math.round(number * 100)}%`
}

function formatGapFlag(value) {
  if (value === true) {
    return 'gap'
  }

  if (value === false) {
    return 'not gap'
  }

  return '--'
}

function formatProposalSummary(proposal) {
  if (!proposal) {
    return '--'
  }

  const confidenceValue = Object.prototype.hasOwnProperty.call(proposal, 'mapping_confidence')
    ? proposal.mapping_confidence
    : proposal.confidence

  return [
    proposal.scope_id_candidate || 'no candidate',
    proposal.match_basis || '--',
    formatConfidence(confidenceValue),
    formatGapFlag(proposal.is_gap_flag)
  ].join(' | ')
}

function formatPriorProposalSummary(priorProposal) {
  if (!priorProposal) {
    return '--'
  }

  if (
    !priorProposal.scope_id_candidate &&
    priorProposal.confidence == null &&
    !priorProposal.match_basis
  ) {
    return '--'
  }

  return [
    priorProposal.scope_id_candidate || 'no candidate',
    priorProposal.match_basis || '--',
    formatConfidence(priorProposal.confidence)
  ].join(' | ')
}

function formatCurrentStateSummary(currentState) {
  if (!currentState) {
    return '--'
  }

  return [
    currentState.mapping_status
      ? `${formatDecisionLabel(currentState.mapping_status)}${currentState.scope_id ? `: ${currentState.scope_id}` : ''}`
      : '--',
    formatConfidence(currentState.confidence)
  ].join(' | ')
}

function formatScopeOptionLabel(scopeOption) {
  if (!scopeOption) {
    return '--'
  }

  const parts = [
    scopeOption.scope_id,
    scopeOption.scope_name,
    scopeOption.scope_family ? `family: ${scopeOption.scope_family}` : null,
    scopeOption.csi_code ? `CSI ${scopeOption.csi_code}` : null
  ].filter(Boolean)

  return parts.join(' | ')
}

function isManualScopeOverride(proposal, selectedScopeId) {
  const chosenScopeId = String(selectedScopeId || '').trim()
  if (!chosenScopeId) {
    return false
  }

  return chosenScopeId !== String(proposal?.scope_id_candidate || '').trim()
}

function summarizeReview(items) {
  const summary = {
    total: items.length,
    gaps: 0,
    review: 0,
    ready: 0
  }

  for (const item of items) {
    const state = getProposalState(item)
    if (state === 'gaps') {
      summary.gaps += 1
    } else if (state === 'review') {
      summary.review += 1
    } else {
      summary.ready += 1
    }
  }

  return summary
}

function matchesFilter(item, filter) {
  if (filter === 'all') {
    return true
  }

  return getProposalState(item) === filter
}

function matchesQuery(item, query) {
  if (!query) {
    return true
  }

  const haystack = [
    item.line_id,
    item.raw_scope_label,
    item.raw_description,
    item.submitted_basis,
    item.proposal?.scope_id_candidate,
    item.proposal?.match_basis
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(query)
}

const ProcurementScopeAliasReview = ({ initialResponseId = '', onNavigate }) => {
  const [responseIdInput, setResponseIdInput] = useState('')
  const [loadedResponseId, setLoadedResponseId] = useState('')
  const [reviewData, setReviewData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [savingLineId, setSavingLineId] = useState('')
  const [error, setError] = useState('')
  const [saveMessage, setSaveMessage] = useState('')
  const [lastSavedAction, setLastSavedAction] = useState(null)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [reviewer, setReviewer] = useState('')
  const [lineNotes, setLineNotes] = useState({})
  const [linePersistence, setLinePersistence] = useState({})
  const [lineScopeOverrides, setLineScopeOverrides] = useState({})

  const items = reviewData?.items || []
  const canonicalScopeOptions = reviewData?.canonical_scope_options || []
  const canonicalScopeIds = new Set(
    canonicalScopeOptions
      .map((option) => String(option?.scope_id || '').trim())
      .filter(Boolean)
  )
  const summary = summarizeReview(items)
  const normalizedQuery = normalizeText(query)
  const filteredItems = items.filter((item) => (
    matchesFilter(item, filter) && matchesQuery(item, normalizedQuery)
  ))

  async function loadReview(targetResponseId = '') {
    const nextResponseId = String(targetResponseId || responseIdInput || '').trim()
    if (!nextResponseId) {
      setError('Enter a response_id to load the review surface.')
      return
    }

    setLoading(true)
    setError('')
    setSaveMessage('')

    try {
      const data = await fetchScopeAliasReview(nextResponseId)
      setReviewData(data)
      setLoadedResponseId(nextResponseId)
      setFilter('all')
      setQuery('')
    } catch (loadError) {
      console.error('Scope alias review load failed:', loadError)
      setError(loadError.message || 'Failed to load scope alias review')
      setReviewData(null)
      setLoadedResponseId('')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const nextResponseId = String(initialResponseId || '').trim()
    if (!nextResponseId || nextResponseId === loadedResponseId) {
      if (!nextResponseId) {
        setResponseIdInput('')
      }
      return
    }

    setResponseIdInput(nextResponseId)
    loadReview(nextResponseId)
  }, [initialResponseId, loadedResponseId])

  function handleSubmit(event) {
    event.preventDefault()
    loadReview()
  }

  function getLineNotes(item) {
    if (Object.prototype.hasOwnProperty.call(lineNotes, item.line_id)) {
      return lineNotes[item.line_id]
    }

    return item.review_decision?.review_notes || ''
  }

  function getPersistenceMode(item) {
    if (Object.prototype.hasOwnProperty.call(linePersistence, item.line_id)) {
      return linePersistence[item.line_id]
    }

    return item.review_decision?.persistence_mode || DEFAULT_PERSISTENCE_MODE
  }

  function getSelectedScopeId(item) {
    if (Object.prototype.hasOwnProperty.call(lineScopeOverrides, item.line_id)) {
      return lineScopeOverrides[item.line_id]
    }

    return item.review_decision?.scope_id || item.proposal?.scope_id_candidate || ''
  }

  function getSelectedScopeOption(item) {
    const selectedScopeId = getSelectedScopeId(item)
    return canonicalScopeOptions.find((option) => option.scope_id === selectedScopeId) || null
  }

  function getBaselineProposal(item) {
    const scopeIdCandidate = item.audit_history?.initial_proposal?.scope_id_candidate
      || item.review_decision?.initial_proposal_scope_id_candidate
      || item.review_decision?.prior_scope_id_candidate
      || item.proposal?.scope_id_candidate

    return {
      scope_id_candidate: scopeIdCandidate || null
    }
  }

  async function submitReviewDecision(item, decision) {
    const nextReviewer = String(reviewer || '').trim()
    if (!nextReviewer) {
      setError('Reviewer is required before writing alias decisions.')
      return
    }

    const selectedScopeId = String(getSelectedScopeId(item) || '').trim()
    if (decision === 'approve' && !selectedScopeId) {
      setError('Approve requires a canonical scope selection or an existing proposal candidate.')
      return
    }

    if (decision === 'approve' && canonicalScopeIds.size > 0 && !canonicalScopeIds.has(selectedScopeId)) {
      setError(`Canonical scope not found: ${selectedScopeId}`)
      return
    }

    const successMessage = decision === 'approve'
      ? `Stored alias approval for ${item.line_id}.`
      : `Stored deferred review state for ${item.line_id}.`

    setSavingLineId(item.line_id)
    setError('')
    setSaveMessage('')
    setLastSavedAction(null)

    try {
      await submitScopeAliasReviewActions(loadedResponseId, {
        reviewer: nextReviewer,
        actions: [
          {
            line_id: item.line_id,
            decision,
            scope_id: decision === 'approve' ? selectedScopeId : undefined,
            persistence_mode: decision === 'approve'
              ? getPersistenceMode(item)
              : DEFAULT_PERSISTENCE_MODE,
            review_notes: getLineNotes(item) || undefined
          }
        ]
      })

      await loadReview(loadedResponseId)
      setSaveMessage(successMessage)
      setLastSavedAction({
        line_id: item.line_id,
        decision,
        message: successMessage
      })
    } catch (saveError) {
      console.error('Scope alias review save failed:', saveError)
      setError(saveError.message || 'Failed to write alias review decision')
    } finally {
      setSavingLineId('')
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Procurement Scope Review</h2>
        <p>
          Review proposed canonical scope matches against vendor-native raw bid lines without
          overwriting the submitted wording, basis, or pricing.
        </p>
      </div>

      <FormSection title="Load Raw Response Review">
        <form className="procurement-review-form" onSubmit={handleSubmit}>
          <label className="procurement-review-input-group">
            <span className="text-strong">Response ID</span>
            <input
              type="text"
              value={responseIdInput}
              onChange={(event) => setResponseIdInput(event.target.value)}
              placeholder="resp_20260319_001"
            />
          </label>
          <div className="actions procurement-review-actions">
            <button
              type="button"
              className="btn"
              onClick={() => onNavigate?.('procurement-review')}
            >
              Back to Procurement Review
            </button>
            <button
              type="submit"
              className="btn primary"
              disabled={loading || !responseIdInput.trim()}
            >
              {loading ? 'Loading Review...' : 'Load Scope Review'}
            </button>
          </div>
        </form>

        <div className="procurement-review-note">
          <strong>Current boundary:</strong> approvals and deferrals write latest-state alias rows
          to `hbfa_procurement_scope_aliases_dev` and append immutable events to
          `hbfa_procurement_scope_alias_review_events_dev`. Raw vendor-native line fields remain
          untouched.
        </div>

        {error && (
          <div className="banner warn">
            <p>{error}</p>
          </div>
        )}

        {saveMessage && (
          <div className="banner info">
            <p>{saveMessage}</p>
          </div>
        )}
      </FormSection>

      {reviewData && (
        <>
          <FormSection title="Review Summary">
            <div className="procurement-review-summary-grid">
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Response</span>
                <span className="procurement-review-stat-value">{loadedResponseId}</span>
              </div>
              <div className="procurement-review-stat">
                <span className="procurement-review-stat-label">Raw Lines</span>
                <span className="procurement-review-stat-value">{summary.total}</span>
              </div>
              <div className="procurement-review-stat procurement-review-stat--warn">
                <span className="procurement-review-stat-label">Gap Flags</span>
                <span className="procurement-review-stat-value">{summary.gaps}</span>
              </div>
              <div className="procurement-review-stat procurement-review-stat--review">
                <span className="procurement-review-stat-label">Needs Review</span>
                <span className="procurement-review-stat-value">{summary.review}</span>
              </div>
              <div className="procurement-review-stat procurement-review-stat--ready">
                <span className="procurement-review-stat-label">Clear Matches</span>
                <span className="procurement-review-stat-value">{summary.ready}</span>
              </div>
            </div>

            <div className="banner info">
              <p>
                Raw wording remains vendor-native. This page writes review decisions to the alias
                layer only, now reads immutable initial proposals plus append-only review history,
                and still does not create leveling outputs.
              </p>
            </div>
          </FormSection>

          <FormSection title="Review Writeback">
            <div className="procurement-review-form">
              <label className="procurement-review-input-group">
                <span className="text-strong">Reviewer</span>
                <input
                  type="text"
                  value={reviewer}
                  onChange={(event) => setReviewer(event.target.value)}
                  placeholder="initials or user id"
                />
              </label>
              <div className="procurement-review-note">
                Approve writes a response-scoped alias row and can optionally promote the same
                alias to `vendor_catalog#vendor_id` for future reuse. A chosen `scope_id`
                overrides the machine proposal without changing raw line fields. Defer writes
                only a response-scoped review state.
              </div>
            </div>
          </FormSection>

          <FormSection title="Alias Review Buffer">
            <div className="procurement-review-toolbar">
              <div className="procurement-review-filters">
                {FILTER_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`sub-nav-item ${filter === option.key ? 'active' : ''}`}
                    onClick={() => setFilter(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <label className="procurement-review-search">
                <span className="text-strong">Search loaded lines</span>
                <input
                  type="text"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="scope label, description, candidate..."
                />
              </label>
            </div>

            {canonicalScopeOptions.length > 0 && (
              <datalist id="procurement-canonical-scope-options">
                {canonicalScopeOptions.map((scopeOption) => (
                  <option
                    key={scopeOption.scope_id}
                    value={scopeOption.scope_id}
                  >
                    {formatScopeOptionLabel(scopeOption)}
                  </option>
                ))}
              </datalist>
            )}

            {items.length === 0 ? (
              <div className="procurement-review-empty">
                No raw lines were found for this response yet.
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="procurement-review-empty">
                No lines match the current filter.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="table procurement-review-table">
                  <thead>
                    <tr>
                      <th>Seq</th>
                      <th>Raw Scope</th>
                      <th>Vendor Description</th>
                      <th>Submitted Basis</th>
                      <th>Proposed Scope</th>
                      <th>Match Basis</th>
                      <th>Confidence</th>
                      <th>Stored Decision</th>
                      <th>Audit Trail</th>
                      <th>Notes</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => {
                      const proposal = item.proposal || {}
                      const state = getProposalState(item)
                      const reviewDecision = item.review_decision || {}
                      const auditHistory = item.audit_history || null
                      const baselineProposal = getBaselineProposal(item)
                      const selectedScopeId = getSelectedScopeId(item)
                      const selectedScopeOption = getSelectedScopeOption(item)
                      const hasManualOverride = isManualScopeOverride(
                        baselineProposal,
                        selectedScopeId
                      )
                      const wasLastSaved = lastSavedAction?.line_id === item.line_id
                      const hasAuditContent = Boolean(
                        auditHistory?.current_state ||
                        auditHistory?.initial_proposal ||
                        auditHistory?.event_count
                      )
                      const isSaving = savingLineId === item.line_id

                      return (
                        <tr
                          key={item.line_id}
                          className={`procurement-review-row procurement-review-row--${state}`}
                        >
                          <td>{item.line_sequence}</td>
                          <td>
                            <div className="procurement-review-cell-title">
                              {item.raw_scope_label || 'Untitled raw scope'}
                            </div>
                            <div className="procurement-review-cell-meta">{item.line_id}</div>
                          </td>
                          <td>
                            <div className="procurement-review-cell-title">
                              {item.raw_description || 'No description provided'}
                            </div>
                            <div className="procurement-review-cell-meta">
                              Amount: {item.submitted_amount ?? '--'}
                              {item.submitted_uom ? ` | UOM: ${item.submitted_uom}` : ''}
                            </div>
                          </td>
                          <td>{item.submitted_basis || '--'}</td>
                          <td>
                            <div className="procurement-review-proposal">
                              <span className={`procurement-review-chip procurement-review-chip--${state}`}>
                                {proposal.scope_id_candidate || 'No candidate'}
                              </span>
                            </div>
                          </td>
                          <td>{proposal.match_basis || '--'}</td>
                          <td>{formatConfidence(proposal.mapping_confidence)}</td>
                          <td>
                            {item.review_decision ? (
                              <div className="procurement-review-cell-title">
                                {formatDecisionLabel(reviewDecision.mapping_status)}
                                {reviewDecision.scope_id ? `: ${reviewDecision.scope_id}` : ''}
                              </div>
                            ) : (
                              <div className="procurement-review-cell-meta">No stored decision</div>
                            )}
                            {reviewDecision.reviewed_at && (
                              <div className="procurement-review-cell-meta">
                                {reviewDecision.reviewer || 'reviewer'} | {reviewDecision.reviewed_at}
                              </div>
                            )}
                            {reviewDecision.scope_id && isManualScopeOverride(
                              baselineProposal,
                              reviewDecision.scope_id
                            ) && (
                              <div className="procurement-review-cell-meta">
                                Manual override from {baselineProposal.scope_id_candidate || 'no candidate'} to {reviewDecision.scope_id}
                              </div>
                            )}
                            {reviewDecision.review_notes && (
                              <div className="procurement-review-cell-meta">
                                {reviewDecision.review_notes}
                              </div>
                            )}
                          </td>
                          <td>
                            {hasAuditContent ? (
                              <details>
                                <summary>
                                  {auditHistory.event_count > 0
                                    ? `${auditHistory.event_count} event${auditHistory.event_count === 1 ? '' : 's'}`
                                    : 'Current state only'}
                                </summary>
                                <div className="procurement-review-cell-meta">
                                  <strong>Immutable initial:</strong>{' '}
                                  {formatProposalSummary(auditHistory.initial_proposal)}
                                </div>
                                {auditHistory.initial_proposal?.captured_at && (
                                  <div className="procurement-review-cell-meta">
                                    Captured {auditHistory.initial_proposal.captured_at}
                                    {auditHistory.initial_proposal.capture_source
                                      ? ` | ${auditHistory.initial_proposal.capture_source}`
                                      : ''}
                                  </div>
                                )}
                                <div className="procurement-review-cell-meta">
                                  <strong>Current state:</strong>{' '}
                                  {formatCurrentStateSummary(auditHistory.current_state)}
                                </div>
                                {auditHistory.current_state?.scope_id && isManualScopeOverride(
                                  auditHistory.initial_proposal,
                                  auditHistory.current_state.scope_id
                                ) && (
                                  <div className="procurement-review-cell-meta">
                                    <strong>Override:</strong>{' '}
                                    {auditHistory.initial_proposal?.scope_id_candidate || 'no candidate'} to {auditHistory.current_state.scope_id}
                                  </div>
                                )}
                                <div className="procurement-review-cell-meta">
                                  <strong>Prior proposal:</strong>{' '}
                                  {formatPriorProposalSummary(auditHistory.current_state?.prior_proposal)}
                                </div>
                                {auditHistory.review_events.map((eventItem) => (
                                  <div
                                    key={eventItem.event_id || `${item.line_id}-${eventItem.reviewed_at}`}
                                    className="procurement-review-cell-meta"
                                  >
                                    <strong>
                                      {eventItem.reviewed_at || eventItem.created_at || '--'}
                                    </strong>
                                    {` | ${eventItem.reviewer || 'reviewer'} | ${formatDecisionLabel(eventItem.decision)} | ${formatDecisionLabel(eventItem.persistence_mode)}`}
                                    <br />
                                    Proposal: {formatProposalSummary(eventItem.proposal_snapshot)}
                                    <br />
                                    Previous: {formatCurrentStateSummary(eventItem.previous_state_snapshot)}
                                    <br />
                                    Result: {formatCurrentStateSummary(eventItem.resulting_state_snapshot)}
                                    {eventItem.resulting_state_snapshot?.scope_id && isManualScopeOverride(
                                      eventItem.proposal_snapshot,
                                      eventItem.resulting_state_snapshot.scope_id
                                    ) && (
                                      <>
                                        <br />
                                        Override: {eventItem.proposal_snapshot?.scope_id_candidate || 'no candidate'} to {eventItem.resulting_state_snapshot.scope_id}
                                      </>
                                    )}
                                  </div>
                                ))}
                              </details>
                            ) : (
                              <div className="procurement-review-cell-meta">
                                No audit history yet
                              </div>
                            )}
                          </td>
                          <td>
                            <label className="procurement-review-input-group">
                              <span className="procurement-review-cell-meta">Review notes</span>
                              <input
                                type="text"
                                value={getLineNotes(item)}
                                onChange={(event) => setLineNotes((current) => ({
                                  ...current,
                                  [item.line_id]: event.target.value
                                }))}
                                placeholder="optional note"
                              />
                            </label>
                          </td>
                          <td>
                            <div className="procurement-review-actions">
                              <label className="procurement-review-input-group">
                                <span className="procurement-review-cell-meta">Canonical scope</span>
                                <input
                                  type="text"
                                  list="procurement-canonical-scope-options"
                                  value={selectedScopeId}
                                  onChange={(event) => setLineScopeOverrides((current) => ({
                                    ...current,
                                    [item.line_id]: event.target.value
                                  }))}
                                  placeholder={proposal.scope_id_candidate || 'type scope_id'}
                                  disabled={isSaving}
                                />
                              </label>
                              {selectedScopeOption && (
                                <div className="procurement-review-cell-meta">
                                  {formatScopeOptionLabel(selectedScopeOption)}
                                </div>
                              )}
                              {hasManualOverride && (
                                <div className="procurement-review-cell-meta">
                                  Approve will override the proposal for this alias context.
                                </div>
                              )}
                              <label className="procurement-review-input-group">
                                <span className="procurement-review-cell-meta">Persistence</span>
                                <select
                                  value={getPersistenceMode(item)}
                                  onChange={(event) => setLinePersistence((current) => ({
                                    ...current,
                                    [item.line_id]: event.target.value
                                  }))}
                                  disabled={isSaving}
                                >
                                  <option value="response_only">Response Only</option>
                                  <option value="vendor_reusable">Vendor Reusable</option>
                                </select>
                              </label>
                              <button
                                type="button"
                                className="btn primary"
                                disabled={isSaving || !selectedScopeId || !reviewer.trim()}
                                onClick={() => submitReviewDecision(item, 'approve')}
                              >
                                {isSaving ? 'Saving...' : 'Approve'}
                              </button>
                              <button
                                type="button"
                                className="btn"
                                disabled={isSaving || !reviewer.trim()}
                                onClick={() => submitReviewDecision(item, 'defer')}
                              >
                                {isSaving ? 'Saving...' : 'Defer'}
                              </button>
                              {wasLastSaved && (
                                <div className="procurement-review-cell-meta">
                                  {lastSavedAction.message}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </FormSection>
        </>
      )}
    </div>
  )
}

export default ProcurementScopeAliasReview
